import { SaleContactDetails } from "./SaleContactData";
import { AgTechIndex, Index } from "@agtech/core/utilities/AgTechUtilities";
import { AgTechQuery, useAgTechQuery, useAgTechQueryCombination } from "@agtech/core/data/queries/AgTechDataQueries";
import { ContactCreditsGetAllCreditsQuery } from "./credits/ContactCreditDataQueries";
import AgTechQueryLoadingSurface from "@agtech/core/data/queries/AgTechQueryLoadingSurface";
import { ContactCredit } from "./credits/ContactCreditData";
import { AgTechContact } from "@agtech/data/contacts/ContactData";
import { ContactDetailsDTO, ContactDataGetAllContactsQuery } from "@agtech/data/contacts/ContactDataQueries";

///
/// DTOs
///

export type SaleContactDetailsDTO = ContactDetailsDTO & {
    credits: ContactCredit[]
}

///
/// Queries
///

export const SaleContactDataGetAllContactsQuery: AgTechQuery<any, AgTechIndex<AgTechContact>> = {
    queryKey: () => ['Contacts'],
    query: (props) => {
        return props.http.getEntity<AgTechContact[], AgTechIndex<AgTechContact>>({
            request: 'Contact',
            convert: (contacts) => {
                return Index(contacts, contact => contact.id);
            }
        });
    },
    content: {
        loadingText: 'Loading contacts...',
        failureText: 'Failed to load contacts.'
    }
}

export const SaleContactDataGetContactDetailsQuery: AgTechQuery<{ contactId: number }, SaleContactDetails> = {
    queryKey: ({ contactId }) => ['ContactDetails', contactId],
    query: (props) => {
        return props.http.getEntity<SaleContactDetailsDTO, SaleContactDetails>({
            request: `Contact/${props.inputData.contactId}/Details`,
            convert: dto => {
                return {
                    ...dto,
                    notes: Index(dto.notes, note => note.id),
                    credits: Index(dto.credits, credit => credit.id)
                };
            }
        });
    },
    content: {
        loadingText: 'Loading contact details...',
        failureText: 'Failed to load contact details.'
    }
}

///
/// Composite Queries
///

export const useSaleContactDataGetContactsQuery = () => {
    return useAgTechQueryCombination({
        firstQuery: { query: ContactDataGetAllContactsQuery},
        secondQuery: { query: ContactCreditsGetAllCreditsQuery},
        projection: (contacts, credits) => {
            const creditsGroupedByContact = Index(credits, c => c.contactId);

            return Object.values(contacts).map(contact => {
                return {
                    ...contact,
                    credits: creditsGroupedByContact[contact.id] ?? []
                }
            });
        }
    })
}

export const useSaleContactDataGetContactDetailsQuery = (contactId: number) => {
    return useAgTechQuery({
        query: SaleContactDataGetContactDetailsQuery,
        queryContext: {
            contactId: contactId
        }
    });
}

///
/// Providers
///

export const SaleContactDetailsProvider = (props: { 
    contactId: number,
    children: (details: SaleContactDetails, refresh: () => Promise<void>) => JSX.Element
}) => {
    const contactDetailsQuery = useSaleContactDataGetContactDetailsQuery(props.contactId);

    return (
        <AgTechQueryLoadingSurface query={contactDetailsQuery}>
            {props.children}
        </AgTechQueryLoadingSurface>
    )
}